import { Clock, CheckCircle, XCircle, Loader2, Eye, X as XIcon, Trash2, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';

interface WorkflowRunCardProps {
  run: {
    runId: string;
    workflowName: string;
    status: 'Pending' | 'Running' | 'Succeeded' | 'Failed' | 'Cancelled';
    startedAt: string;
    finishedAt?: string;
    triggeredBy: string;
    parameters: Record<string, any>;
  };
  onViewLogs: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  compact?: boolean;
}

export function WorkflowRunCard({ run, onViewLogs, onCancel, onDelete, compact = false }: WorkflowRunCardProps) {
  const [showYaml, setShowYaml] = useState(false);

  const parameters = run?.parameters && typeof run.parameters === 'object' ? run.parameters : {};
  const status = run?.status ?? 'Pending';

  const statusIcons = {
    Pending: <Clock className="w-4 h-4" />,
    Running: <Loader2 className="w-4 h-4 animate-spin" />,
    Succeeded: <CheckCircle className="w-4 h-4" />,
    Failed: <XCircle className="w-4 h-4" />,
    Cancelled: <XIcon className="w-4 h-4" />,
  };

  const statusColors = {
    Pending: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    Running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    Succeeded: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    Failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    Cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-500',
  };

  // Filter out internal/hidden parameters; skip undefined keys
  const hiddenParams = ['runId', 'triggeredBy', 'maxironYamlB64', 'configOverrides'];
  const visibleParameters = Object.entries(parameters).filter(
    ([key]) => key != null && key !== '' && !hiddenParams.includes(key)
  );
  
  // Decode and show configuration exactly as stored (no unwrap or re-format)
  const formattedConfig = useMemo(() => {
    try {
      if (parameters.maxironYamlB64) {
        return atob(parameters.maxironYamlB64);
      }
      if (parameters.configOverrides) {
        return parameters.configOverrides;
      }
      return null;
    } catch (error) {
      console.error('Failed to decode configuration:', error);
      return parameters.configOverrides || null;
    }
  }, [parameters.maxironYamlB64, parameters.configOverrides]);

  const formatDuration = () => {
    const start = new Date(run?.startedAt ?? 0).getTime();
    const end = run?.finishedAt ? new Date(run.finishedAt).getTime() : Date.now();
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const canCancel = status === 'Pending' || status === 'Running';
  const canDelete = status === 'Succeeded' || status === 'Failed' || status === 'Cancelled';

  if (compact) {
    const paramSummary = visibleParameters
      .map(([k, v]) => `${k}: ${typeof v === 'string' && v.length > 30 ? v.substring(0, 30) + '…' : String(v)}`)
      .join(' | ');

    return (
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`p-1.5 rounded flex-shrink-0 ${statusColors[status] ?? statusColors.Pending}`}>
            {statusIcons[status] ?? statusIcons.Pending}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{run?.workflowName ?? 'Workflow'}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{formatDuration()}</span>
              {run?.triggeredBy && (
                <span className="text-xs text-gray-500 dark:text-gray-400">| {run.triggeredBy}</span>
              )}
            </div>
            {paramSummary && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{paramSummary}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onViewLogs}>
            <Eye className="w-4 h-4" />
          </Button>
          {canCancel && onCancel && (
            <Button size="sm" variant="outline" onClick={onCancel} className="text-orange-600 hover:text-orange-700 dark:text-orange-500 dark:hover:text-orange-400 border-orange-300 hover:border-orange-400 dark:border-orange-700 dark:hover:border-orange-600">
              <XIcon className="w-4 h-4" />
            </Button>
          )}
          {canDelete && onDelete && (
            <Button size="sm" variant="outline" onClick={onDelete} >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{run?.workflowName ?? 'Workflow'}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">Run ID: {run?.runId ?? '—'}</p>
        </div>
        <Badge variant="outline" className={statusColors[status] ?? statusColors.Pending}>
          <span className="flex items-center gap-1.5">
            {statusIcons[status] ?? statusIcons.Pending}
            {status}
          </span>
        </Badge>
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 dark:text-gray-400">Started:</span>
          <span className="text-gray-900 dark:text-gray-100">{run?.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}</span>
        </div>
        {run?.finishedAt && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Finished:</span>
            <span className="text-gray-900 dark:text-gray-100">{new Date(run.finishedAt).toLocaleString()}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 dark:text-gray-400">Duration:</span>
          <span className="text-gray-900 dark:text-gray-100">{formatDuration()}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 dark:text-gray-400">Triggered by:</span>
          <span className="text-gray-900 dark:text-gray-100">{run?.triggeredBy ?? '—'}</span>
        </div>
      </div>

      {visibleParameters.length > 0 && (
        <div className="mb-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parameters:</p>
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2 text-xs font-mono">
            {visibleParameters.map(([key, value], idx) => (
              <div key={key || `param-${idx}`} className="flex gap-2">
                <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                <span className="text-gray-900 dark:text-gray-100">
                  {typeof value === 'string' && value.length > 50 
                    ? `${value.substring(0, 50)}...` 
                    : JSON.stringify(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {formattedConfig && (
        <div className="mb-3">
          <button
            onClick={() => setShowYaml(!showYaml)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            {showYaml ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <FileText className="w-4 h-4" />
            Configuration
          </button>
          {showYaml && (
            <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 text-xs font-mono overflow-auto max-h-96 border border-gray-700">
              <pre className="text-gray-100 whitespace-pre overflow-x-auto min-w-0">{formattedConfig}</pre>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-auto pt-3 border-t border-gray-200 dark:border-gray-700">
        <Button onClick={onViewLogs} variant="outline" className="flex-1">
          <Eye className="w-4 h-4 mr-2" />
          View Logs
        </Button>
        {canCancel && onCancel && (
          <Button onClick={onCancel} variant="outline" className="text-orange-600 hover:text-orange-700 dark:text-orange-500 dark:hover:text-orange-400 border-orange-300 hover:border-orange-400 dark:border-orange-700 dark:hover:border-orange-600">
            <XIcon className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        )}
        {canDelete && onDelete && (
          <Button onClick={onDelete} variant="outline">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}


